import React, { useMemo } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from 'components/mui';
import { useTheme, alpha } from '@mui/material/styles';
import { SREDReportRow } from 'api/sred';
import { buildPivotData, getActiveConsultants, formatProjectLabel } from '../libs';

interface SREDPivotTableProps {
  rows: SREDReportRow[];
  isLoading: boolean;
  hasFetched: boolean;
}

function formatHrs(value: number): string {
  if (!value) return '';
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function SREDPivotTable({ rows, isLoading, hasFetched }: SREDPivotTableProps) {
  const theme = useTheme();

  const activeConsultants = useMemo(() => getActiveConsultants(rows), [rows]);
  const pivotData = useMemo(() => buildPivotData(rows), [rows]);

  const grandTotals = useMemo(() => {
    const totals: Record<string, { reg: number; ot: number }> = {};
    for (const c of activeConsultants) totals[c] = { reg: 0, ot: 0 };
    let totalHrs = 0;
    for (const prj of pivotData) {
      for (const c of activeConsultants) {
        totals[c].reg += prj.totals[c]?.reg || 0;
        totals[c].ot += prj.totals[c]?.ot || 0;
      }
      totalHrs += prj.totalHrs;
    }
    return { consultants: totals, totalHrs: Math.round(totalHrs * 100) / 100 };
  }, [pivotData, activeConsultants]);

  if (isLoading) {
    return (
      <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (hasFetched && rows.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
        <Typography>No SRED data found for the selected date range.</Typography>
      </Paper>
    );
  }

  if (!hasFetched || rows.length === 0) return null;

  const numCons = activeConsultants.length;

  return (
    <TableContainer component={Paper} elevation={1}>
      <Table size="small" sx={{ minWidth: 800 }}>
        <TableHead>
          {/* Consultant name row */}
          <TableRow>
            <TableCell sx={{ fontWeight: 600, minWidth: 300 }} />
            {activeConsultants.map((con) => (
              <TableCell
                key={con}
                colSpan={2}
                align="center"
                sx={{ fontWeight: 600 }}
              >
                {con}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ fontWeight: 600 }}>
              Totals
            </TableCell>
          </TableRow>
          {/* Reg/OT sub-header row */}
          <TableRow>
            <TableCell sx={{ fontSize: '0.75rem' }} />
            {activeConsultants.map((con) => (
              <React.Fragment key={con}>
                <TableCell
                  align="right"
                  sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}
                >
                  Reg
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}
                >
                  OT
                </TableCell>
              </React.Fragment>
            ))}
            <TableCell
              align="right"
              sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}
            >
              Hrs
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pivotData.map((prj) => (
            <React.Fragment key={prj.projectNum}>
              {/* Project header row */}
              <TableRow>
                <TableCell
                  colSpan={numCons * 2 + 2}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    fontWeight: 'bold',
                    color: 'primary.dark',
                    fontSize: '0.875rem',
                  }}
                >
                  {formatProjectLabel(prj.projectTitle)}
                </TableCell>
              </TableRow>

              {/* Case rows */}
              {prj.cases.map((c) => (
                <TableRow key={c.caseId} hover>
                  <TableCell sx={{ pl: 3, fontSize: '0.8125rem' }}>
                    <a
                      href={`/cantrackv3/cases/${c.caseId}`}
                      style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
                    >
                      Case {c.caseId}
                    </a>
                    &nbsp;{c.caseTitle.substring(0, 42)}
                  </TableCell>
                  {activeConsultants.map((con) => (
                    <React.Fragment key={con}>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem' }}>
                        {formatHrs(c.consultants[con]?.reg || 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem' }}>
                        {formatHrs(c.consultants[con]?.ot || 0)}
                      </TableCell>
                    </React.Fragment>
                  ))}
                  <TableCell align="right" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                    {formatHrs(c.totalHrs)}
                  </TableCell>
                </TableRow>
              ))}

              {/* Project subtotal row */}
              <TableRow>
                <TableCell
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    fontWeight: 'bold',
                    pl: 3,
                  }}
                >
                  {formatProjectLabel(prj.projectTitle)} Totals
                </TableCell>
                {activeConsultants.map((con) => (
                  <React.Fragment key={con}>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        fontWeight: 'bold',
                      }}
                    >
                      {formatHrs(prj.totals[con]?.reg || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        fontWeight: 'bold',
                      }}
                    >
                      {formatHrs(prj.totals[con]?.ot || 0)}
                    </TableCell>
                  </React.Fragment>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    fontWeight: 'bold',
                  }}
                >
                  {formatHrs(prj.totalHrs)}
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}

          {/* Grand total row */}
          <TableRow>
            <TableCell
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                fontWeight: 'bold',
                fontSize: '0.875rem',
              }}
            >
              GRAND TOTALS
            </TableCell>
            {activeConsultants.map((con) => (
              <React.Fragment key={con}>
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    fontWeight: 'bold',
                  }}
                >
                  {formatHrs(grandTotals.consultants[con]?.reg || 0)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    fontWeight: 'bold',
                  }}
                >
                  {formatHrs(grandTotals.consultants[con]?.ot || 0)}
                </TableCell>
              </React.Fragment>
            ))}
            <TableCell
              align="right"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                fontWeight: 'bold',
                fontSize: '0.875rem',
              }}
            >
              {formatHrs(grandTotals.totalHrs)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SREDPivotTable;
